import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import SuperDocEditor from './SuperDocEditor';
import './SuperDocDocumentManager.css';

/**
 * SuperDocDocumentManager - Manages document templates using SuperDoc editor
 * Replaces DocumentsTab with SuperDoc-based editing
 */
const SuperDocDocumentManager = ({ propertyId }) => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [showEditor, setShowEditor] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [editingDocumentName, setEditingDocumentName] = useState(null);
    const [documentName, setDocumentName] = useState('');
    
    // Modal state for creating new template type
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newTemplate, setNewTemplate] = useState({
        name: '',
        description: '',
        icon: '📄',
        key: ''
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [customCategories, setCustomCategories] = useState([]);
    const [boilerplateAvailable, setBoilerplateAvailable] = useState(false);
    const [boilerplateInfo, setBoilerplateInfo] = useState(null);
    const [propertyDocInfo, setPropertyDocInfo] = useState(null);
    

    // Available icons for templates
    const availableIcons = ['📄', '📋', '📝', '📑', '📃', '🏠', '💰', '⚖️', '🔧', '⚠️', '🔄', '📊', '📈', '🔒', '✅', '📢', '👥', '🏢', '🚪', '🔑'];

    useEffect(() => {
        fetchDocuments();
        loadCustomCategories();
        if (propertyId) {
            checkBoilerplateAndPropertyDoc();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propertyId]);

    const loadCustomCategories = () => {
        // Load custom categories from localStorage
        const saved = localStorage.getItem('customDocumentCategories');
        if (saved) {
            try {
                setCustomCategories(JSON.parse(saved));
            } catch (e) {
                console.error('Error loading custom categories:', e);
            }
        }
    };

    const saveCustomCategories = (categories) => {
        setCustomCategories(categories);
        localStorage.setItem('customDocumentCategories', JSON.stringify(categories));
    };

    const checkBoilerplateAndPropertyDoc = async () => {
        if (!propertyId) return;
        
        try {
            // Check if property document exists or if boilerplate is available
            const response = await fetch(`${API_BASE_URL}/api/document-layers/property/${propertyId}/document`);
            if (response.ok) {
                const data = await response.json();
                if (data.boilerplate_template_id) {
                    setBoilerplateAvailable(true);
                    setBoilerplateInfo({
                        id: data.boilerplate_template_id,
                        name: data.boilerplate_name || 'Lease Template'
                    });
                    
                    // AUTO-CREATE property document if boilerplate exists but property doc doesn't
                    if (!data.property_doc_id && data.boilerplate_template_id) {
                        console.log('Auto-creating property document from boilerplate...');
                        await createPropertyDocFromBoilerplate(true); // silent mode
                    }
                }
                if (data.property_doc_id) {
                    setPropertyDocInfo({
                        id: data.property_doc_id,
                        boilerplate_id: data.boilerplate_template_id
                    });
                }
            }
        } catch (err) {
            console.error('Error checking boilerplate:', err);
        }
    };

    const createPropertyDocFromBoilerplate = async (silent = false) => {
        if (!propertyId || !boilerplateInfo) return;
        
        if (!silent) setUploading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/document-layers/property/${propertyId}/document-from-boilerplate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    boilerplate_template_id: boilerplateInfo.id
                })
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Failed to create property document' }));
                throw new Error(errorData.error || 'Failed to create property document');
            }
            
            const data = await response.json();
            setPropertyDocInfo({
                id: data.property_doc_id,
                boilerplate_id: data.boilerplate_template_id
            });
            
            // Refresh documents to show the new property document
            await fetchDocuments();
            
            if (!silent) {
                alert('Property document created from boilerplate template! You can now edit it.');
            }
        } catch (err) {
            console.error('Error creating property document:', err);
            if (!silent) {
                alert(`Failed to create property document: ${err.message}`);
            }
        } finally {
            if (!silent) setUploading(false);
        }
    };

    const fetchDocuments = async () => {
        setLoading(true);
        try {
            // Fetch documents from backend
            const url = propertyId 
                ? `${API_BASE_URL}/api/documents?property_id=${propertyId}`
                : `${API_BASE_URL}/api/documents`;
            
            const response = await fetch(url);
            if (response.ok) {
                const data = await response.json();
                setDocuments(data);
                
                // If we have a property document from the layered system, add it to the list
                if (propertyId && propertyDocInfo && propertyDocInfo.id) {
                    // Check if lease document already exists in the list
                    const leaseDoc = data.find(d => d.category === 'lease');
                    if (!leaseDoc) {
                        // Add property document as a virtual document entry
                        setDocuments([...data, {
                            id: `prop_doc_${propertyDocInfo.id}`,
                            name: 'Lease Agreement (Property-Specific)',
                            category: 'lease',
                            property_id: propertyId,
                            is_property_document: true,
                            property_doc_id: propertyDocInfo.id,
                            boilerplate_id: propertyDocInfo.boilerplate_id
                        }]);
                    }
                }
            }
        } catch (err) {
            console.error('Error fetching documents:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (file, category = null, documentName = null) => {
        if (!file || !file.name.endsWith('.docx')) {
            alert('Please upload a .docx file');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (propertyId) {
                formData.append('property_id', propertyId);
            }
            if (category) {
                formData.append('category', category);
            }
            if (documentName) {
                formData.append('name', documentName);
            }

            const response = await fetch(`${API_BASE_URL}/api/documents/upload`, {
                method: 'POST',
                body: formData,
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Upload failed' }));
                throw new Error(errorData.error || 'Upload failed');
            }

            const newDoc = await response.json();
            
            // Refresh the document list to get the updated state
            await fetchDocuments();
            
            alert('Document uploaded successfully');
            return newDoc;
        } catch (err) {
            console.error('Error uploading document:', err);
            alert(`Failed to upload document: ${err.message}`);
            return null;
        } finally {
            setUploading(false);
        }
    };
    

    const handleCreateTemplate = async () => {
        // Validate form
        if (!newTemplate.name.trim()) {
            alert('Please enter a template name');
            return;
        }
        if (!newTemplate.key.trim()) {
            alert('Please enter a category key');
            return;
        }

        // Check if key already exists
        const allCategories = [...defaultCategories, ...customCategories];
        if (allCategories.some(c => c.key === newTemplate.key)) {
            alert('A template with this key already exists. Please use a different key.');
            return;
        }

        // Create the new category
        const newCategory = {
            key: newTemplate.key.toLowerCase().replace(/\s+/g, '_'),
            icon: newTemplate.icon,
            name: newTemplate.name.trim(),
            description: newTemplate.description.trim() || 'Custom document template',
            isCustom: true
        };

        // Save to custom categories
        const updatedCategories = [...customCategories, newCategory];
        saveCustomCategories(updatedCategories);

        // If a file was selected, upload it with the new category
        if (selectedFile) {
            await handleUpload(selectedFile, newCategory.key);
        }

        // Reset modal state
        setShowCreateModal(false);
        setNewTemplate({ name: '', description: '', icon: '📄', key: '' });
        setSelectedFile(null);
    };

    const handleDeleteCategory = (categoryKey) => {
        if (!window.confirm('Are you sure you want to delete this template category? This will not delete uploaded documents.')) {
            return;
        }

        const updatedCategories = customCategories.filter(c => c.key !== categoryKey);
        saveCustomCategories(updatedCategories);
    };

    const handleEdit = async (doc) => {
        // If this is a property document from the layered system, get the resolved version
        if (doc.is_property_document && propertyId) {
            try {
                // Get the resolved property document (boilerplate + property overrides merged)
                const response = await fetch(`${API_BASE_URL}/api/document-layers/property/${propertyId}/document/resolved`);
                if (response.ok) {
                    // The endpoint returns a file, so we need to create a blob URL
                    const blob = await response.blob();
                    const file = new File([blob], `${doc.name || 'lease'}.docx`, { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
                    
                    setSelectedDocument({
                        ...doc,
                        resolvedFile: file,
                        isResolved: true,
                        note: 'This is a property-specific document. Edits will create property overrides, not modify the boilerplate.'
                    });
                    setDocumentName(doc.name || '');
                    setShowEditor(true);
                    return;
                }
            } catch (err) {
                console.error('Error loading resolved property document:', err);
                // Fall through to regular edit
            }
        }
        
        // Regular document edit
        setSelectedDocument(doc);
        setDocumentName(doc.name || '');
        setShowEditor(true);
    };

    const handleRenameDocument = async (docId, newName) => {
        if (!newName || newName.trim() === '') {
            alert('Document name cannot be empty');
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/documents/${docId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ name: newName.trim() })
            });

            if (!response.ok) {
                throw new Error('Failed to rename document');
            }

            // Update local state
            setDocuments(documents.map(doc => 
                doc.id === docId ? { ...doc, name: newName.trim() } : doc
            ));

            // Update selected document if it's the one being renamed
            if (selectedDocument && selectedDocument.id === docId) {
                setSelectedDocument({ ...selectedDocument, name: newName.trim() });
                setDocumentName(newName.trim());
            }

            setEditingDocumentName(null);
            alert('Document renamed successfully');
        } catch (err) {
            console.error('Error renaming document:', err);
            alert(`Failed to rename document: ${err.message}`);
        }
    };

    const handleSaveDocument = async (documentBlob, documentId) => {
        try {
            // Ensure we have a valid blob
            if (!documentBlob || !(documentBlob instanceof Blob)) {
                throw new Error('Invalid document blob');
            }

            // Ensure we have a valid document ID
            const docId = documentId || selectedDocument?.id;
            if (!docId) {
                throw new Error('Document ID is required');
            }

            // Ensure filename ends with .docx
            let filename = selectedDocument?.name || 'document.docx';
            if (!filename.toLowerCase().endsWith('.docx')) {
                filename = filename.replace(/\.[^/.]+$/, '') + '.docx';
            }

            // Create a File object from the blob with correct MIME type
            const file = new File([documentBlob], filename, {
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            });

            const formData = new FormData();
            formData.append('file', file, filename);

            console.log('Saving document:', { docId, filename, fileSize: file.size, fileType: file.type });

            const response = await fetch(`${API_BASE_URL}/api/documents/${docId}`, {
                method: 'PUT',
                body: formData,
            });

            if (!response.ok) {
                // Try to get error message from response
                let errorMessage = 'Save failed';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.error || errorMessage;
                } catch (e) {
                    errorMessage = `Save failed with status ${response.status}: ${response.statusText}`;
                }
                console.error('Save failed:', errorMessage);
                throw new Error(errorMessage);
            }

            const responseData = await response.json();
            console.log('Document saved successfully:', responseData);
            
            alert('Document saved successfully');
            setShowEditor(false);
            setSelectedDocument(null);
            fetchDocuments(); // Refresh list
        } catch (err) {
            console.error('Error saving document:', err);
            alert(`Failed to save document: ${err.message}`);
        }
    };

    const handleDelete = async (docId) => {
        if (!window.confirm('Are you sure you want to delete this document?')) {
            return;
        }

        try {
            console.log('Deleting document:', docId);
            const response = await fetch(`${API_BASE_URL}/api/documents/${docId}`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                // Try to get error message from response
                let errorMessage = 'Delete failed';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.error || errorMessage;
                } catch (e) {
                    errorMessage = `Delete failed with status ${response.status}: ${response.statusText}`;
                }
                console.error('Delete failed:', errorMessage);
                throw new Error(errorMessage);
            }

            const responseData = await response.json();
            console.log('Document deleted successfully:', responseData);
            
            // Update the documents list
            setDocuments(documents.filter(d => d.id !== docId));
            alert('Document deleted successfully');
            
            // Refresh the list to ensure consistency
            fetchDocuments();
        } catch (err) {
            console.error('Error deleting document:', err);
            alert(`Failed to delete document: ${err.message}`);
        }
    };

    const defaultCategories = [
        { key: 'lease', icon: '📄', name: 'Standard Lease Agreement', description: '12-month residential lease template' },
        { key: 'eviction', icon: '⚠️', name: 'Eviction Notice', description: '3-day, 30-day, 60-day notices' },
        { key: 'renewal', icon: '🔄', name: 'Lease Renewal', description: 'Renewal and amendment forms' },
        { key: 'deposit', icon: '💰', name: 'Security Deposit Return', description: 'Itemized deduction statement' },
        { key: 'maintenance', icon: '🔧', name: 'Maintenance Notice', description: 'Entry and repair notices' },
        { key: 'rent_increase', icon: '📝', name: 'Rent Increase Notice', description: '30/60/90 day rent notices' },
    ];

    // Combine default and custom categories
    const documentCategories = [...defaultCategories, ...customCategories];

    if (showEditor && selectedDocument) {
        const isEditingName = editingDocumentName === selectedDocument.id;
        
        return (
            <div className="superdoc-document-manager" key={`editor-${selectedDocument.id}`}>
                <div className="editor-header">
                    <div className="document-name-section">
                        {isEditingName ? (
                            <div className="rename-input-group">
                                <input
                                    type="text"
                                    value={documentName}
                                    onChange={(e) => setDocumentName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleRenameDocument(selectedDocument.id, documentName);
                                        } else if (e.key === 'Escape') {
                                            setDocumentName(selectedDocument.name);
                                            setEditingDocumentName(null);
                                        }
                                    }}
                                    className="document-name-input"
                                    autoFocus
                                />
                                <button
                                    onClick={() => handleRenameDocument(selectedDocument.id, documentName)}
                                    className="rename-save-btn"
                                >
                                    ✓
                                </button>
                                <button
                                    onClick={() => {
                                        setDocumentName(selectedDocument.name);
                                        setEditingDocumentName(null);
                                    }}
                                    className="rename-cancel-btn"
                                >
                                    ✕
                                </button>
                            </div>
                        ) : (
                            <div className="document-name-display">
                                <h3>Editing: {selectedDocument.name}</h3>
                                <button
                                    onClick={() => {
                                        setEditingDocumentName(selectedDocument.id);
                                        setDocumentName(selectedDocument.name);
                                    }}
                                    className="rename-btn"
                                    title="Rename document"
                                >
                                    ✏️
                                </button>
                            </div>
                        )}
                    </div>
                    <button 
                        onClick={() => {
                            setShowEditor(false);
                            setSelectedDocument(null);
                            setEditingDocumentName(null);
                        }}
                        className="back-button"
                    >
                        ← Back to Documents
                    </button>
                </div>
                {selectedDocument.note && (
                    <div style={{
                        marginBottom: '16px',
                        padding: '12px',
                        background: '#e7f0ff',
                        border: '1px solid #1457f5',
                        borderRadius: '8px',
                        fontSize: '14px',
                        color: '#1457f5'
                    }}>
                        <strong>ℹ️ {selectedDocument.note}</strong>
                    </div>
                )}
                <SuperDocEditor
                    key={`superdoc-${selectedDocument.id}`}
                    documentId={selectedDocument.is_property_document ? `prop_doc_${selectedDocument.property_doc_id}` : selectedDocument.id}
                    documentFile={selectedDocument.resolvedFile || (selectedDocument.download_url ? `${API_BASE_URL}${selectedDocument.download_url}` : null)}
                    onSave={handleSaveDocument}
                    onCancel={() => {
                        setShowEditor(false);
                        setSelectedDocument(null);
                        setEditingDocumentName(null);
                    }}
                />
            </div>
        );
    }

    return (
        <div className="superdoc-document-manager">
            <div className="documents-header">
                <div>
                    <h2>Legal Document Templates</h2>
                    {propertyId && (
                        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '14px' }}>
                            Context: Property ID {propertyId}
                        </p>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {propertyId && boilerplateAvailable && !propertyDocInfo && (
                        <button 
                            className="upload-doc-btn"
                            onClick={createPropertyDocFromBoilerplate}
                            disabled={uploading}
                            style={{ 
                                background: '#10b981', 
                                color: 'white',
                                border: 'none'
                            }}
                        >
                            {uploading ? 'Creating...' : `📋 Use Boilerplate: ${boilerplateInfo?.name || 'Lease Template'}`}
                        </button>
                    )}
                    <button 
                        className="upload-doc-btn"
                        onClick={() => setShowCreateModal(true)}
                    >
                        + Create Template Type
                    </button>
                </div>
            </div>
            
            {propertyId && boilerplateAvailable && !propertyDocInfo && (
                <div style={{
                    margin: '16px 0',
                    padding: '12px 16px',
                    background: '#e7f0ff',
                    border: '1px solid #1457f5',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div>
                        <strong style={{ color: '#1457f5' }}>💡 Boilerplate Template Available</strong>
                        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '14px' }}>
                            A boilerplate lease template exists. Click "Use Boilerplate" to create a property-specific version that inherits from it.
                        </p>
                    </div>
                </div>
            )}

            <div className="document-templates-grid">
                {documentCategories.map(category => {
                    const doc = documents.find(d => d.category === category.key);
                    const isLeaseCategory = category.key === 'lease';
                    const hasPropertyDoc = isLeaseCategory && propertyDocInfo;
                    const hasBoilerplate = isLeaseCategory && boilerplateAvailable;
                    
                    return (
                        <div key={category.key} className="document-template-card">
                            <div className="doc-icon">{category.icon}</div>
                            <h4>{category.name}</h4>
                            <p>{category.description}</p>
                            
                            {/* Show hierarchy for lease documents */}
                            {isLeaseCategory && propertyId && (
                                <div style={{
                                    marginTop: '12px',
                                    padding: '10px',
                                    background: '#f8f9fa',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span style={{ color: '#64748b' }}>📋 Boilerplate:</span>
                                            <span style={{ fontWeight: 600, color: hasBoilerplate ? '#10b981' : '#ef4444' }}>
                                                {hasBoilerplate ? `✓ ${boilerplateInfo?.name || 'Available'}` : 'Not Found'}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span style={{ color: '#64748b' }}>🏠 Property:</span>
                                            <span style={{ fontWeight: 600, color: hasPropertyDoc ? '#1457f5' : '#f59e0b' }}>
                                                {hasPropertyDoc ? '✓ Created' : '→ Will inherit from boilerplate'}
                                            </span>
                                        </div>
                                        {hasPropertyDoc && hasBoilerplate && (
                                            <div style={{
                                                marginTop: '4px',
                                                padding: '6px',
                                                background: '#e7f0ff',
                                                borderRadius: '4px',
                                                fontSize: '11px',
                                                color: '#1457f5'
                                            }}>
                                                ↳ Inherits from: {boilerplateInfo?.name}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            
                            {category.isCustom && (
                                <span className="custom-badge">Custom</span>
                            )}
                            <div className="doc-actions">
                                {doc ? (
                                    <>
                                        <button 
                                            className="doc-btn" 
                                            onClick={() => handleEdit(doc)}
                                        >
                                            ✏️ Edit
                                        </button>
                                        <button 
                                            className="doc-btn danger" 
                                            onClick={() => handleDelete(doc.id)}
                                        >
                                            🗑️ Delete
                                        </button>
                                    </>
                                ) : (
                                    <div className="doc-btn-group">
                                        {/* For lease category with propertyId, show different options */}
                                        {isLeaseCategory && propertyId ? (
                                            hasPropertyDoc ? (
                                                <button 
                                                    className="doc-btn"
                                                    onClick={async () => {
                                                        // Load the property document for editing
                                                        // First, we need to get the resolved document
                                                        try {
                                                            const response = await fetch(`${API_BASE_URL}/api/document-layers/property/${propertyId}/document`);
                                                            const data = await response.json();
                                                            if (data.property_doc_id) {
                                                                // For now, we'll need to create a way to edit property documents
                                                                // This is a placeholder - we need an endpoint to get the resolved document
                                                                alert('Property document editing coming soon. For now, property documents inherit from boilerplate.');
                                                            }
                                                        } catch (err) {
                                                            console.error('Error loading property document:', err);
                                                        }
                                                    }}
                                                >
                                                    ✏️ Edit Property Version
                                                </button>
                                            ) : hasBoilerplate ? (
                                                <button 
                                                    className="doc-btn"
                                                    onClick={() => createPropertyDocFromBoilerplate()}
                                                    disabled={uploading}
                                                    style={{ background: '#10b981' }}
                                                >
                                                    {uploading ? 'Creating...' : '📋 Create from Boilerplate'}
                                                </button>
                                            ) : (
                                                <label className="doc-btn">
                                                    📤 Upload Boilerplate First
                                                    <input
                                                        type="file"
                                                        accept=".docx"
                                                        style={{ display: 'none' }}
                                                        onChange={(e) => {
                                                            const file = e.target.files?.[0];
                                                            if (file) {
                                                                // Upload as boilerplate (no property_id)
                                                                handleUpload(file, category.key);
                                                            }
                                                            e.target.value = '';
                                                        }}
                                                    />
                                                </label>
                                            )
                                        ) : (
                                            <label className="doc-btn">
                                                📤 Upload
                                                <input
                                                    type="file"
                                                    accept=".docx"
                                                    style={{ display: 'none' }}
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) {
                                                            handleUpload(file, category.key);
                                                        }
                                                        e.target.value = '';
                                                    }}
                                                />
                                            </label>
                                        )}
                                        {category.isCustom && (
                                            <button 
                                                className="doc-btn danger-outline"
                                                onClick={() => handleDeleteCategory(category.key)}
                                                title="Remove this template type"
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {loading && (
                <div className="loading-overlay">
                    <div className="spinner"></div>
                    <p>Loading documents...</p>
                </div>
            )}

            {/* Create New Template Modal */}
            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content create-template-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Create New Template Type</h2>
                            <button className="modal-close" onClick={() => setShowCreateModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Template Name *</label>
                                <input
                                    type="text"
                                    placeholder="e.g., Move-In Checklist"
                                    value={newTemplate.name}
                                    onChange={(e) => {
                                        const name = e.target.value;
                                        setNewTemplate({
                                            ...newTemplate,
                                            name,
                                            key: name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
                                        });
                                    }}
                                    className="form-input"
                                />
                            </div>

                            <div className="form-group">
                                <label>Category Key *</label>
                                <input
                                    type="text"
                                    placeholder="e.g., move_in_checklist"
                                    value={newTemplate.key}
                                    onChange={(e) => setNewTemplate({
                                        ...newTemplate,
                                        key: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
                                    })}
                                    className="form-input"
                                />
                                <small className="form-help">Unique identifier (letters, numbers, underscores only)</small>
                            </div>

                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    placeholder="Brief description of this template type..."
                                    value={newTemplate.description}
                                    onChange={(e) => setNewTemplate({
                                        ...newTemplate,
                                        description: e.target.value
                                    })}
                                    className="form-textarea"
                                    rows={2}
                                />
                            </div>

                            <div className="form-group">
                                <label>Icon</label>
                                <div className="icon-picker">
                                    {availableIcons.map(icon => (
                                        <button
                                            key={icon}
                                            type="button"
                                            className={`icon-option ${newTemplate.icon === icon ? 'selected' : ''}`}
                                            onClick={() => setNewTemplate({ ...newTemplate, icon })}
                                        >
                                            {icon}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Upload Template File (Optional)</label>
                                <div className="file-upload-area">
                                    {selectedFile ? (
                                        <div className="selected-file">
                                            <span className="file-icon">📄</span>
                                            <span className="file-name">{selectedFile.name}</span>
                                            <button 
                                                className="remove-file-btn"
                                                onClick={() => setSelectedFile(null)}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ) : (
                                        <label className="file-drop-zone">
                                            <input
                                                type="file"
                                                accept=".docx"
                                                style={{ display: 'none' }}
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                        if (!file.name.endsWith('.docx')) {
                                                            alert('Please select a .docx file');
                                                            return;
                                                        }
                                                        setSelectedFile(file);
                                                    }
                                                    e.target.value = '';
                                                }}
                                            />
                                            <div className="drop-zone-content">
                                                <span className="drop-icon">📤</span>
                                                <span>Click to select a .docx file</span>
                                                <small>or leave empty to add a file later</small>
                                            </div>
                                        </label>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn-secondary"
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setNewTemplate({ name: '', description: '', icon: '📄', key: '' });
                                    setSelectedFile(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn-primary"
                                onClick={handleCreateTemplate}
                                disabled={!newTemplate.name.trim() || !newTemplate.key.trim() || uploading}
                            >
                                {uploading ? 'Creating...' : 'Create Template'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default SuperDocDocumentManager;
